import { useState, useMemo, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { useConfiguration } from "../../../components/ConfigurationContext";
import { useBranch } from "../../../components/BranchContext";
import { accountingService } from "../../../../../services/accounting";
import {
  IListJournalEntriesRequest,
  IJournalEntryListItem,
  ICompleteJournalEntry,
  IDraftJournalEntryRequest,
  IPostJournalEntryRequest,
  IGetJournalEntryRequest,
  IDeleteJournalEntryRequest,
  IPostDraftJournalEntryRequest,
  IReverseJournalEntryRequest,
} from "../../../accounting.interface";
import moment from "moment";
import { toLower } from "lodash";

// Updated interfaces to match API responses
export interface JournalFilters {
  dateFrom?: string;
  dateTo?: string;
  selectedBranch?: string;
  sourceModule?: string;
  status?: "draft" | "posted" | "all";
  postedBy?: string;
  journalEntryId?: string;
  referenceId?: string;
  narrationContains?: string;
}

export interface JournalTableData extends IJournalEntryListItem {
  totalDebit: number;
  totalCredit: number;
}

export interface ConfirmationDialogState {
  open: boolean;
  type: "delete" | "reverse" | "post" | null;
  journal: IJournalEntryListItem | null;
}

export interface UseJournalsReturn {
  // State
  filters: JournalFilters;
  selectedJournal: ICompleteJournalEntry | null;
  isDetailDialogOpen: boolean;
  isFormDialogOpen: boolean;
  isLoading: boolean;
  tableData: JournalTableData[];
  confirmationDialog: ConfirmationDialogState;
  page: number;
  rowsPerPage: number;
  filteredJournals: IJournalEntryListItem[];
  totalCount: number;

  // Handlers
  handleFiltersChange: (newFilters: Partial<JournalFilters>) => void;
  handleResetFilters: () => void;
  handleApplyFilters: () => void;
  handleJournalClick: (journal: IJournalEntryListItem) => void;
  handleCloseDetailDialog: () => void;
  handleOpenFormDialog: () => void;
  handleCloseFormDialog: () => void;
  handleEditJournal: (journal: IJournalEntryListItem) => void;
  handleReverseJournal: (journal: IJournalEntryListItem) => void;
  handleDeleteJournal: (journal: IJournalEntryListItem) => void;
  handlePostJournal: (journal: IJournalEntryListItem) => void;
  handleSubmitJournal: (journalData: any) => Promise<void>;
  handleCopyJournalId: (journalId: string) => void;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
  handleCloseConfirmationDialog: () => void;
  handleConfirmAction: () => Promise<void>;
  refreshData: () => Promise<void>;
}

const initialFilters: JournalFilters = {
  dateFrom: undefined,
  dateTo: undefined,
  selectedBranch: "organization",
  sourceModule: undefined,
  status: "all",
  postedBy: undefined,
  journalEntryId: undefined,
  referenceId: undefined,
  narrationContains: undefined,
};

export const useJournals = (): UseJournalsReturn => {
  const { journalSettings } = useConfiguration();
  const { selectedBranch } = useBranch();

  // State
  const [journals, setJournals] = useState<IJournalEntryListItem[]>([]);
  const [filters, setFilters] = useState<JournalFilters>(initialFilters);
  const [selectedJournal, setSelectedJournal] =
    useState<ICompleteJournalEntry | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [editingJournal, setEditingJournal] =
    useState<IJournalEntryListItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Confirmation dialog states
  const [confirmationDialog, setConfirmationDialog] =
    useState<ConfirmationDialogState>({
      open: false,
      type: null,
      journal: null,
    });

  // Fetch journals from API
  const fetchJournals = useCallback(async () => {
    setIsLoading(true);
    try {
      const requestData: IListJournalEntriesRequest = {
        pageIndex: page - 1, // API uses 0-based indexing
        pageSize: rowsPerPage,
        sortField: "created_at",
        sortOrder: "desc",
        selected_branch_id:
          filters.selectedBranch === "organization"
            ? undefined
            : filters.selectedBranch,
        status: filters.status === "all" ? undefined : toLower(filters.status),
        searchQuery: filters.narrationContains,
        startDate: filters.dateFrom
          ? moment(filters.dateFrom).valueOf()
          : undefined,
        endDate: filters.dateTo ? moment(filters.dateTo).valueOf() : undefined,
      };

      // Add additional filters if provided
      if (filters.sourceModule) {
        requestData.filters = [
          {
            field: "source_module",
            operator: "equals",
            value: filters.sourceModule,
          },
        ];
      }

      const response = await accountingService.listJournals(requestData);
      setJournals(response.data);
      setTotalCount(response.data.length); // Note: API doesn't return total count yet
    } catch (error: any) {
      console.error("Failed to fetch journals:", error);
      toast.error(error.message || "Failed to fetch journal entries");
      setJournals([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  // Fetch journal details
  const fetchJournalDetails = useCallback(async (journalId: string) => {
    try {
      const requestData: IGetJournalEntryRequest = {
        journal_entry_id: journalId,
      };
      const response = await accountingService.getJournal(requestData);
      setSelectedJournal(response.data);
    } catch (error: any) {
      console.error("Failed to fetch journal details:", error);
      toast.error(error.message || "Failed to fetch journal details");
    }
  }, []);

  // Effect to fetch data when dependencies change
  useEffect(() => {
    fetchJournals();
  }, [fetchJournals]);

  // Computed data
  const filteredJournals = useMemo(() => {
    return journals; // API already applies filters
  }, [journals]);

  const tableData: JournalTableData[] = useMemo(() => {
    return journals.map((journal: any) => {
      // Extract journal type from tags
      const journalTypeTag = journal.tags?.find(
        (tag: any) => tag.tag_type === "JOURNAL_TYPE"
      );
      const journalType = journalTypeTag?.tag_value || "Unknown";

      // Use total_amount from API instead of calculating manually
      const totalAmount = journal.total_amount || 0;

      return {
        ...journal,
        totalDebit: totalAmount, // For backward compatibility, though we should use total_amount
        totalCredit: totalAmount,
        formattedJournalType: journalType,
        formattedStatus: journal.status,
        formattedDate: journal.entry_date,
        formattedAmount: `${journal.currency} ${totalAmount.toFixed(2)}`,
        createdByUser: journal.created_by_email,
        branchDisplayName: journal.branch_name || "Unknown Branch",
      };
    });
  }, [journals]);

  // Handlers
  const handleFiltersChange = useCallback(
    (newFilters: Partial<JournalFilters>) => {
      setFilters((prev) => ({ ...prev, ...newFilters }));
      setPage(1); // Reset to first page when filters change
    },
    []
  );

  const handleResetFilters = useCallback(() => {
    setFilters(initialFilters);
    setPage(1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    // Filters are applied automatically via fetchJournals
    fetchJournals();
  }, [fetchJournals]);

  const handleJournalClick = useCallback(
    async (journal: IJournalEntryListItem) => {
      await fetchJournalDetails(journal._id);
      setIsDetailDialogOpen(true);
    },
    [fetchJournalDetails]
  );

  const handleCloseDetailDialog = useCallback(() => {
    setIsDetailDialogOpen(false);
    setSelectedJournal(null);
  }, []);

  const handleOpenFormDialog = useCallback(() => {
    setEditingJournal(null);
    setIsFormDialogOpen(true);
  }, []);

  const handleCloseFormDialog = useCallback(() => {
    setIsFormDialogOpen(false);
    setEditingJournal(null);
  }, []);

  const handleEditJournal = useCallback((journal: IJournalEntryListItem) => {
    if (journal.status !== "draft") {
      toast.warning("Only draft entries can be edited");
      return;
    }
    setEditingJournal(journal);
    setIsFormDialogOpen(true);
    setIsDetailDialogOpen(false);
  }, []);

  const handleReverseJournal = useCallback((journal: IJournalEntryListItem) => {
    if (journal.status !== "posted") {
      toast.error("Only posted entries can be reversed");
      return;
    }
    setConfirmationDialog({
      open: true,
      type: "reverse",
      journal,
    });
  }, []);

  const handleDeleteJournal = useCallback((journal: IJournalEntryListItem) => {
    if (journal.status !== "draft") {
      toast.error("Only draft entries can be deleted");
      return;
    }
    setConfirmationDialog({
      open: true,
      type: "delete",
      journal,
    });
  }, []);

  const handlePostJournal = useCallback((journal: IJournalEntryListItem) => {
    if (toLower(journal.status) !== "draft") {
      toast.error("Only draft entries can be posted");
      return;
    }
    setConfirmationDialog({
      open: true,
      type: "post",
      journal,
    });
  }, []);

  const handleSubmitJournal = useCallback(
    async (journalData: any) => {
      setIsLoading(true);
      try {
        if (editingJournal) {
          // Update existing journal (not yet implemented in API)
          toast.warning("Journal editing not yet implemented");
        } else {
          // Create new journal
          if (journalData.status === "Draft") {
            const draftData: IDraftJournalEntryRequest = {
              journal_date: journalData.entry_date,
              branch_id: journalData.branch_id,
              currency: journalData.currency,
              journal_type: journalData.journal_type,
              narration: journalData.narration,
              reference_id: journalData.reference_id,
              journal_lines: journalData.journal_lines,
            };
            await accountingService.createDraftJournal(draftData);
            toast.success("Draft journal entry created successfully");
          } else {
            const postData: IPostJournalEntryRequest = {
              journal_date: journalData.entry_date,
              branch_id: journalData.branch_id,
              currency: journalData.currency,
              journal_type: journalData.journal_type,
              narration: journalData.narration,
              reference_id: journalData.reference_id,
              journal_lines: journalData.journal_lines,
            };
            await accountingService.postJournal(postData);
            toast.success("Journal entry posted successfully");
          }
        }

        // Refresh data
        await fetchJournals();
        setIsFormDialogOpen(false);
        setEditingJournal(null);
      } catch (error: any) {
        console.error("Failed to save journal:", error);
        toast.error(error.message || "Failed to save journal entry");
      } finally {
        setIsLoading(false);
      }
    },
    [editingJournal, fetchJournals]
  );

  const handleCopyJournalId = useCallback((journalId: string) => {
    navigator.clipboard.writeText(journalId);
    toast.success("Journal ID copied to clipboard");
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setRowsPerPage(newPageSize);
    setPage(1); // Reset to first page
  }, []);

  const handleCloseConfirmationDialog = useCallback(() => {
    setConfirmationDialog({
      open: false,
      type: null,
      journal: null,
    });
  }, []);

  const handleConfirmAction = useCallback(async () => {
    const { type, journal } = confirmationDialog;
    if (!journal || !type) return;

    setIsLoading(true);
    try {
      switch (type) {
        case "delete":
          {
            const deleteRequest: IDeleteJournalEntryRequest = {
              journal_entry_id: journal._id,
              deletion_reason: "Deleted by user", // You can add a reason input field if needed
            };
            await accountingService.deleteJournal(deleteRequest);
            toast.success("Draft journal entry deleted successfully");
          }
          break;
        case "reverse":
          {
            const reverseRequest: IReverseJournalEntryRequest = {
              journal_entry_id: journal._id,
              reversal_reason: "Reversed by user", // You can add a reason input field if needed
              reversal_date: moment().valueOf(),
            };
            const response = await accountingService.reverseJournal(
              reverseRequest
            );
            toast.success(
              `Journal entry reversed successfully. New entry: ${response.data.reversing_journal_entry_number}`
            );
          }
          break;
        case "post":
          {
            const postDraftData: IPostDraftJournalEntryRequest = {
              journal_entry_id: journal._id,
            };
            await accountingService.postDraftJournal(postDraftData);
            toast.success("Journal entry posted successfully to the ledger");
          }
          break;
      }

      handleCloseConfirmationDialog();
      await fetchJournals();
    } catch (error: any) {
      console.error(`Failed to ${type} journal:`, error);
      toast.error(error.message || `Failed to ${type} journal`);
    } finally {
      setIsLoading(false);
    }
  }, [confirmationDialog, fetchJournals, handleCloseConfirmationDialog]);

  const refreshData = useCallback(async () => {
    await fetchJournals();
  }, [fetchJournals]);

  return {
    // State
    filters,
    selectedJournal,
    isDetailDialogOpen,
    isFormDialogOpen,
    isLoading,
    tableData,
    confirmationDialog,
    page,
    rowsPerPage,
    filteredJournals,
    totalCount,

    // Handlers
    handleFiltersChange,
    handleResetFilters,
    handleApplyFilters,
    handleJournalClick,
    handleCloseDetailDialog,
    handleOpenFormDialog,
    handleCloseFormDialog,
    handleEditJournal,
    handleReverseJournal,
    handleDeleteJournal,
    handlePostJournal,
    handleSubmitJournal,
    handleCopyJournalId,
    handlePageChange,
    handlePageSizeChange,
    handleCloseConfirmationDialog,
    handleConfirmAction,
    refreshData,
  };
};
